import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { visitorLogAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import DateRangePicker from '../../components/common/DateRangePicker';
import { countries } from '../../data/countries';

type ActiveTab = 'today' | 'all-time';
type PeriodPreset = '7d' | '30d' | '90d' | 'custom';

interface VisitorEntry {
  visitor_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  country_code: string | null;
  device_type: string | null;
  browser: string | null;
  ip_address: string | null;
  first_seen: string;
  last_seen: string;
  total_page_views: number;
  campaign_slug: string | null;
}

interface TodayHit {
  visitor_id: string;
  page_url: string;
  page_title: string | null;
  time_on_page: number | null;
  visited_at: string;
  ip_address: string | null;
  country: string | null;
  country_code: string | null;
  device_type: string | null;
  browser: string | null;
  campaign_slug: string | null;
  name: string | null;
  email: string | null;
  page_views: number;
}

interface VisitorPage {
  page_url: string;
  page_title: string | null;
  referrer: string | null;
  time_on_page: number | null;
  load_time: number | null;
  session_id: string | null;
  created_at: string;
}

interface VisitorDetailData {
  visitor: {
    visitor_id: string;
    ip_address: string | null;
    country: string | null;
    city: string | null;
    device_type: string | null;
    browser: string | null;
    os: string | null;
    campaign_slug: string | null;
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    first_seen: string;
    last_seen: string;
    total_page_views: number;
    name: string | null;
    email: string | null;
    phone: string | null;
  };
  pages: VisitorPage[];
}

interface SessionGroup {
  sessionId: string;
  pages: VisitorPage[];
  startTime: string;
  endTime: string;
  totalDuration: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

// ── Helpers ──────────────────────────────────────────────────
const countryNameToCode = (name: string): string | null => {
  if (!name) return null;
  if (name.length === 2) return name.toUpperCase();
  const found = countries.find(c => c.name.toLowerCase() === name.toLowerCase());
  return found?.code || null;
};

const countryFlag = (code: string) => {
  if (!code || code.length !== 2) return '🌐';
  const upper = code.toUpperCase();
  return String.fromCodePoint(0x1F1E6 + upper.charCodeAt(0) - 65) +
    String.fromCodePoint(0x1F1E6 + upper.charCodeAt(1) - 65);
};

const resolveCountry = (country: string | null, country_code: string | null) => {
  const code = country_code || (country ? countryNameToCode(country) : null);
  const display = country || country_code || null;
  return { code, display };
};

const getDeviceIcon = (device: string | null): string => {
  const d = (device || '').toLowerCase();
  if (d.includes('mobile') || d.includes('phone')) return 'bi-phone';
  if (d.includes('tablet') || d.includes('ipad')) return 'bi-tablet';
  return 'bi-laptop';
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (dateStr: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDuration = (seconds: number | null) => {
  if (seconds == null || seconds === 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

const extractPath = (url: string) => {
  try { return new URL(url).pathname || '/'; } catch { return url || '/'; }
};

const getAvatarLetter = (visitor: { name: string | null; visitor_id: string }) =>
  visitor.name ? visitor.name.charAt(0).toUpperCase() : '?';

const getAvatarColor = (visitorId: string) => {
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];
  return colors[visitorId.charCodeAt(0) % colors.length];
};

const isLiveNow = (lastSeen: string): boolean => {
  return Date.now() - new Date(lastSeen).getTime() < 5 * 60 * 1000;
};

const getDurationColor = (seconds: number | null): string => {
  if (!seconds || seconds < 5) return '#d1d5db';
  if (seconds < 30) return '#9ca3af';
  if (seconds < 120) return '#3b82f6';
  if (seconds < 300) return '#10b981';
  return '#8b5cf6';
};

const groupPagesBySessions = (pages: VisitorPage[]): SessionGroup[] => {
  if (!pages || pages.length === 0) return [];
  const sessions: SessionGroup[] = [];
  let currentSessionId = pages[0].session_id || 'session-0';
  let currentGroup: VisitorPage[] = [pages[0]];

  for (let i = 1; i < pages.length; i++) {
    const page = pages[i];
    const prevPage = pages[i - 1];
    const sid = page.session_id || null;
    const prevSid = prevPage.session_id || null;
    const timeDiff = new Date(page.created_at).getTime() - new Date(prevPage.created_at).getTime();
    const isNewSession = sid && prevSid ? sid !== prevSid : timeDiff > 30 * 60 * 1000;

    if (isNewSession) {
      const totalDuration = currentGroup.reduce((sum, p) => sum + (p.time_on_page || 0), 0);
      sessions.push({
        sessionId: currentSessionId,
        pages: currentGroup,
        startTime: currentGroup[0].created_at,
        endTime: currentGroup[currentGroup.length - 1].created_at,
        totalDuration,
      });
      currentGroup = [page];
      currentSessionId = sid || `session-${sessions.length}`;
    } else {
      currentGroup.push(page);
    }
  }

  const totalDuration = currentGroup.reduce((sum, p) => sum + (p.time_on_page || 0), 0);
  sessions.push({
    sessionId: currentSessionId,
    pages: currentGroup,
    startTime: currentGroup[0].created_at,
    endTime: currentGroup[currentGroup.length - 1].created_at,
    totalDuration,
  });

  return sessions;
};

const UrlCell: React.FC<{ url: string; title?: string | null }> = ({ url, title }) => {
  const [copied, setCopied] = useState(false);
  const displayUrl = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div>
      {title && (
        <div style={{ fontSize: '10px', color: '#9ca3af', lineHeight: 1.3, marginBottom: '2px', fontWeight: 500 }}>
          {title}
        </div>
      )}
      <div className="d-flex align-items-center gap-1">
        <span
          title={url}
          style={{
            fontSize: '12px', fontFamily: 'monospace', maxWidth: '260px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            display: 'block', color: '#4B5563', cursor: 'default',
          }}
        >
          {displayUrl}
        </span>
        <button
          onClick={copy}
          title="Copy URL"
          style={{
            background: 'none', border: 'none', padding: '0 2px',
            color: copied ? '#10b981' : '#9ca3af', cursor: 'pointer',
            lineHeight: 1, flexShrink: 0,
          }}
        >
          <i className={`bi ${copied ? 'bi-check' : 'bi-copy'}`} style={{ fontSize: '11px' }}></i>
        </button>
      </div>
    </div>
  );
};

const IDENTIFY_CODE = `// Call after successful login
CloudAyn.identify({
  name: 'User Full Name',
  email: 'user@example.com',
  phone: '+1234567890',   // optional
  userId: 'your-internal-id'  // optional
});`;

const getPeriodDates = (preset: PeriodPreset): [string, string] => {
  const end = new Date();
  const start = new Date();
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  start.setDate(start.getDate() - days);
  return [start.toISOString().split('T')[0], end.toISOString().split('T')[0]];
};

// ── LiveBadge ──────────────────────────────────────────────────
const LiveBadge: React.FC = () => (
  <span className="d-inline-flex align-items-center gap-1 ms-1"
    style={{
      background: '#dcfce7', color: '#16a34a', fontSize: '10px', fontWeight: 700,
      padding: '1px 6px', borderRadius: '20px', letterSpacing: '0.3px',
    }}
  >
    <span style={{
      width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a',
      animation: 'live-green-pulse 1.5s ease-in-out infinite', flexShrink: 0,
    }}></span>
    LIVE
  </span>
);

// ── Timeline ──────────────────────────────────────────────────
const VisitorTimeline: React.FC<{ detail: VisitorDetailData }> = ({ detail }) => {
  const sessions = groupPagesBySessions(detail.pages);
  const maxTimeOnPage = Math.max(...detail.pages.map(p => p.time_on_page || 0), 1);

  if (sessions.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="bi bi-file-earmark-x d-block mb-2 fs-4"></i>
        No page history available
      </div>
    );
  }

  return (
    <div>
      {sessions.map((session, sIdx) => (
        <div key={session.sessionId} className="mb-4">
          {/* Session header */}
          <div
            className="d-flex align-items-center gap-2 mb-3 px-1"
            style={{
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}
          >
            <div
              style={{
                width: '24px', height: '24px', borderRadius: '50%',
                background: '#3b82f6', color: 'white', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700, flexShrink: 0,
              }}
            >
              {sIdx + 1}
            </div>
            <div className="flex-grow-1">
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                Session {sIdx + 1}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>
                {formatDateTime(session.startTime)}
                {' · '}{session.pages.length} {session.pages.length === 1 ? 'page' : 'pages'}
                {session.totalDuration > 0 && ` · ${formatDuration(session.totalDuration)}`}
              </div>
            </div>
          </div>

          {/* Pages timeline */}
          <div className="ps-2">
            {session.pages.map((page, pIdx) => {
              const isLast = pIdx === session.pages.length - 1;
              const barWidth = maxTimeOnPage > 0
                ? Math.max(4, Math.round(((page.time_on_page || 0) / maxTimeOnPage) * 100))
                : 4;
              const isFirstInSession = pIdx === 0;

              return (
                <div key={pIdx} className="d-flex" style={{ position: 'relative' }}>
                  {/* Timeline line + dot */}
                  <div className="flex-shrink-0 d-flex flex-column align-items-center" style={{ width: '24px', marginRight: '12px' }}>
                    <div style={{
                      width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
                      background: isFirstInSession ? '#3b82f6' : '#94a3b8',
                      border: '2px solid white',
                      boxShadow: '0 0 0 2px ' + (isFirstInSession ? '#3b82f620' : '#94a3b820'),
                      marginTop: '3px',
                    }}></div>
                    {!isLast && (
                      <div style={{ width: '2px', flexGrow: 1, background: '#e2e8f0', minHeight: '24px', marginTop: '4px' }}></div>
                    )}
                  </div>

                  {/* Page content */}
                  <div
                    className="flex-grow-1 pb-3"
                    style={{ borderBottom: isLast ? 'none' : '1px dashed #f1f5f9' }}
                  >
                    {/* Time badge */}
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '3px', fontWeight: 500 }}>
                      {formatTime(page.created_at)}
                      {isFirstInSession && sIdx === 0 && (
                        <span style={{
                          marginLeft: '6px', background: '#dbeafe', color: '#1d4ed8',
                          fontSize: '10px', fontWeight: 600, padding: '0 5px', borderRadius: '4px',
                        }}>Landing</span>
                      )}
                      {isLast && (
                        <span style={{
                          marginLeft: '6px', background: '#fef3c7', color: '#92400e',
                          fontSize: '10px', fontWeight: 600, padding: '0 5px', borderRadius: '4px',
                        }}>Exit</span>
                      )}
                    </div>

                    {/* Page title */}
                    {page.page_title && (
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>
                        {page.page_title}
                      </div>
                    )}

                    {/* Page URL */}
                    <div
                      style={{
                        fontSize: '12px', fontFamily: 'monospace', color: '#4b6280',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: '100%', marginBottom: '6px',
                      }}
                      title={page.page_url}
                    >
                      {extractPath(page.page_url)}
                    </div>

                    {/* Duration bar */}
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <div style={{ flex: 1, height: '4px', background: '#f1f5f9', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${barWidth}%`, height: '100%',
                          background: getDurationColor(page.time_on_page),
                          borderRadius: '2px',
                          transition: 'width 0.5s ease',
                        }}></div>
                      </div>
                      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, flexShrink: 0, minWidth: '32px' }}>
                        {formatDuration(page.time_on_page)}
                      </span>
                    </div>

                    {/* Meta row: load time + referrer */}
                    <div className="d-flex flex-wrap gap-2">
                      {page.load_time != null && (
                        <span style={{
                          fontSize: '10px', color: '#94a3b8', background: '#f8fafc',
                          padding: '1px 6px', borderRadius: '4px', border: '1px solid #e2e8f0',
                        }}>
                          <i className="bi bi-lightning me-1"></i>{page.load_time}ms load
                        </span>
                      )}
                      {page.referrer && isFirstInSession && (
                        <span style={{
                          fontSize: '10px', color: '#94a3b8', background: '#f8fafc',
                          padding: '1px 6px', borderRadius: '4px', border: '1px solid #e2e8f0',
                          maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                          title={page.referrer}
                        >
                          <i className="bi bi-box-arrow-in-right me-1"></i>from {extractPath(page.referrer)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────
const VisitorLogPage: React.FC = () => {
  useDocumentTitle('Visitor Log');
  const { currentAsset } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<ActiveTab>('today');

  // Today tab state
  const [todayHits, setTodayHits] = useState<TodayHit[]>([]);
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayPage, setTodayPage] = useState(1);
  const [todayPagination, setTodayPagination] = useState<PaginationInfo | null>(null);

  // All Time tab state
  const [visitors, setVisitors] = useState<VisitorEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);

  // Filter state
  const [period, setPeriod] = useState<PeriodPreset>('30d');
  const [showCustom, setShowCustom] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateFromObj, setDateFromObj] = useState<Date | null>(null);
  const [dateToObj, setDateToObj] = useState<Date | null>(null);

  // Integration panel
  const [showIntegration, setShowIntegration] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Visitor detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVisitorId, setDrawerVisitorId] = useState<string | null>(null);
  const [visitorDetail, setVisitorDetail] = useState<VisitorDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // ── Open detail drawer ──
  const openDetail = useCallback(async (visitorId: string) => {
    if (!currentAsset) return;
    setDrawerVisitorId(visitorId);
    setDrawerOpen(true);
    setVisitorDetail(null);
    setDetailLoading(true);
    try {
      const res = await visitorLogAPI.getDetail(currentAsset.id, visitorId);
      setVisitorDetail(res.data.data || res.data);
    } catch {
      toast.error('Failed to load visitor details');
    } finally {
      setDetailLoading(false);
    }
  }, [currentAsset]);

  const closeDetail = () => {
    setDrawerOpen(false);
    setDrawerVisitorId(null);
    setVisitorDetail(null);
    // Remove ?visitor param from URL
    const next = new URLSearchParams(searchParams);
    next.delete('visitor');
    setSearchParams(next, { replace: true });
  };

  // ── Deep-link: ?visitor=ID ──
  useEffect(() => {
    const visitorParam = searchParams.get('visitor');
    if (visitorParam && currentAsset && !drawerOpen) {
      openDetail(visitorParam);
    }
  }, [searchParams, currentAsset]);

  // ── Fetch today hits ──
  const fetchTodayHits = useCallback(async () => {
    if (!currentAsset) return;
    setTodayLoading(true);
    try {
      const res = await visitorLogAPI.getToday(currentAsset.id, todayPage, 50);
      const data = res.data.data || res.data;
      setTodayHits(data.data || data.visitors || data || []);
      if (data.pagination) setTodayPagination(data.pagination);
    } catch {
      toast.error('Failed to load today\'s hits');
    } finally {
      setTodayLoading(false);
    }
  }, [currentAsset, todayPage]);

  // ── Fetch all-time visitors ──
  const fetchVisitors = useCallback(async (from = dateFrom, to = dateTo) => {
    if (!currentAsset) return;
    setLoading(true);
    try {
      const res = await visitorLogAPI.list(currentAsset.id, page, 50, search, from, to);
      const data = res.data.data || res.data;
      setVisitors(data.data || data.visitors || data || []);
      if (data.pagination) setPagination(data.pagination);
    } catch {
      toast.error('Failed to load visitor log');
    } finally {
      setLoading(false);
    }
  }, [currentAsset, page, search, dateFrom, dateTo]);

  useEffect(() => { if (activeTab === 'today') fetchTodayHits(); }, [fetchTodayHits, activeTab]);
  useEffect(() => { if (activeTab === 'all-time') fetchVisitors(); }, [fetchVisitors, activeTab]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const selectPeriod = (p: PeriodPreset) => {
    setPeriod(p);
    if (p === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      const [from, to] = getPeriodDates(p);
      setDateFrom(from);
      setDateTo(to);
      setDateFromObj(new Date(from));
      setDateToObj(new Date(to));
      setPage(1);
      fetchVisitors(from, to);
    }
  };

  const applyCustomDate = () => {
    if (dateFrom && dateTo) { setPage(1); fetchVisitors(dateFrom, dateTo); }
  };

  const clearFilters = () => {
    setPeriod('30d');
    setShowCustom(false);
    setDateFrom(''); setDateTo('');
    setDateFromObj(null); setDateToObj(null);
    setSearch(''); setSearchInput('');
    setPage(1);
    fetchVisitors('', '');
  };

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setDateFromObj(start);
    setDateToObj(end);
    setDateFrom(start ? start.toISOString().split('T')[0] : '');
    setDateTo(end ? end.toISOString().split('T')[0] : '');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(IDENTIFY_CODE).then(() => {
      setCodeCopied(true);
      toast.success('Code copied');
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  if (!currentAsset) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">
          <i className="bi bi-exclamation-circle fs-1 text-muted d-block mb-3"></i>
          <h5>No asset selected</h5>
          <p className="text-muted">Please select an asset to view the visitor log.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <h4 className="fw-bold mb-1">
              <i className="bi bi-people-fill me-2 text-primary"></i>Visitor Log
            </h4>
            <p className="text-muted mb-0 small">
              All visitors for <strong>{currentAsset.name}</strong>
            </p>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left me-1"></i>Back
          </button>
        </div>

        {/* Tabs */}
        <ul className="nav nav-pills mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'today' ? 'active' : ''}`}
              onClick={() => setActiveTab('today')}
            >
              <i className="bi bi-clock-history me-2"></i>Today
              {todayPagination && (
                <span className="badge ms-2" style={{ background: activeTab === 'today' ? 'rgba(255,255,255,0.25)' : '#6366f120', color: activeTab === 'today' ? '#fff' : '#6366f1', fontSize: '10px' }}>
                  {todayPagination.total.toLocaleString()} visitors
                </span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'all-time' ? 'active' : ''}`}
              onClick={() => setActiveTab('all-time')}
            >
              <i className="bi bi-people me-2"></i>All Time
              {pagination && (
                <span className="badge ms-2" style={{ background: activeTab === 'all-time' ? 'rgba(255,255,255,0.25)' : '#6366f120', color: activeTab === 'all-time' ? '#fff' : '#6366f1', fontSize: '10px' }}>
                  {pagination.total.toLocaleString()}
                </span>
              )}
            </button>
          </li>
        </ul>

        {/* ── TODAY TAB ── */}
        {activeTab === 'today' && (
          <>
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="bi bi-activity me-2 text-success"></i>
                  Today's Page Hits
                  {todayPagination && (
                    <span className="text-muted fw-normal ms-2">
                      ({todayPagination.total.toLocaleString()} unique visitors)
                    </span>
                  )}
                </h6>
                <button className="btn btn-sm btn-outline-secondary" onClick={fetchTodayHits}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
              </div>
              <div className="card-body p-0">
                {todayLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading…</span>
                    </div>
                  </div>
                ) : todayHits.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-calendar-x fs-1 text-muted d-block mb-3"></i>
                    <h6>No hits recorded today yet</h6>
                    <p className="text-muted small mb-0">Visits will appear here as they happen.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover table-sm mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '100px' }}>Time</th>
                          <th>Visitor</th>
                          <th>Page</th>
                          <th>Country</th>
                          <th>Device</th>
                          <th className="text-center">Views</th>
                          <th className="text-end">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayHits.map(hit => {
                          const { code, display } = resolveCountry(hit.country, hit.country_code);
                          const live = isLiveNow(hit.visited_at);
                          return (
                            <tr
                              key={`${hit.visitor_id}-${hit.page_url}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => openDetail(hit.visitor_id)}
                            >
                              <td>
                                <small className="text-muted font-monospace">{formatTime(hit.visited_at)}</small>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                                    style={{ width: '28px', height: '28px', fontSize: '11px', backgroundColor: getAvatarColor(hit.visitor_id) }}
                                  >
                                    {hit.name ? hit.name.charAt(0).toUpperCase() : '?'}
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '12px', fontWeight: 500 }} className="d-flex align-items-center gap-1">
                                      {hit.name || <span className="text-muted">Anonymous</span>}
                                      {live && <LiveBadge />}
                                    </div>
                                    {hit.email && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{hit.email}</div>}
                                    {!hit.email && hit.ip_address && (
                                      <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{hit.ip_address}</div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td style={{ maxWidth: '280px' }}>
                                <UrlCell url={hit.page_url} title={hit.page_title} />
                              </td>
                              <td>
                                {display ? (
                                  <span title={display}>
                                    {code ? countryFlag(code) : '🌐'}{' '}
                                    <small className="text-muted">{display}</small>
                                  </span>
                                ) : <span className="text-muted">—</span>}
                              </td>
                              <td>
                                <i className={`bi ${getDeviceIcon(hit.device_type)}`} title={hit.device_type || undefined}></i>
                                {hit.browser && <small className="text-muted ms-1">{hit.browser}</small>}
                              </td>
                              <td className="text-center">
                                {hit.page_views > 1 ? (
                                  <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: '11px' }}>
                                    {hit.page_views}×
                                  </span>
                                ) : (
                                  <span className="text-muted small">1</span>
                                )}
                              </td>
                              <td className="text-end">
                                <small className="text-muted">{formatDuration(hit.time_on_page)}</small>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Today Pagination */}
            {todayPagination && todayPagination.total_pages > 1 && (
              <nav className="mt-3">
                <ul className="pagination pagination-sm justify-content-center mb-0">
                  <li className={`page-item ${todayPage <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setTodayPage(p => p - 1)}>
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {Array.from({ length: Math.min(todayPagination.total_pages, 7) }, (_, i) => (
                    <li key={i + 1} className={`page-item ${todayPage === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setTodayPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${todayPage >= todayPagination.total_pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setTodayPage(p => p + 1)}>
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}

        {/* ── ALL TIME TAB ── */}
        {activeTab === 'all-time' && (
          <>
            {/* Filter bar */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body py-3">
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <form onSubmit={handleSearch} className="d-flex gap-2 flex-grow-1" style={{ minWidth: '220px', maxWidth: '320px' }}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search name, email, IP…"
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                    />
                    <button type="submit" className="btn btn-sm btn-outline-primary px-3">
                      <i className="bi bi-search"></i>
                    </button>
                  </form>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small fw-medium">Period:</span>
                    <div className="btn-group btn-group-sm">
                      {(['7d', '30d', '90d'] as PeriodPreset[]).map(p => (
                        <button
                          key={p}
                          className={`btn ${period === p && !showCustom ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => selectPeriod(p)}
                        >
                          {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                        </button>
                      ))}
                      <button
                        className={`btn ${showCustom ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => selectPeriod('custom')}
                      >
                        <i className="bi bi-calendar-range me-1"></i>Custom
                      </button>
                    </div>
                  </div>
                  {(search || dateFrom || dateTo) && (
                    <button className="btn btn-sm btn-outline-danger" onClick={clearFilters}>
                      <i className="bi bi-x-circle me-1"></i>Clear
                    </button>
                  )}
                </div>
                {showCustom && (
                  <div className="mt-3 pt-3 border-top">
                    <DateRangePicker
                      startDate={dateFromObj}
                      endDate={dateToObj}
                      onChange={handleDateRangeChange}
                      onApply={applyCustomDate}
                      onClear={() => {
                        setDateFromObj(null); setDateToObj(null);
                        setDateFrom(''); setDateTo('');
                        setShowCustom(false); setPeriod('30d');
                        fetchVisitors('', '');
                      }}
                      maxDate={new Date()}
                      showActions
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Integration Panel */}
            <div className="card border-0 shadow-sm mb-4">
              <div
                className="card-header bg-white d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowIntegration(!showIntegration)}
              >
                <h6 className="mb-0">
                  <i className="bi bi-code-slash me-2 text-primary"></i>Identify Your Visitors
                </h6>
                <i className={`bi ${showIntegration ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`}></i>
              </div>
              {showIntegration && (
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-12 col-lg-5">
                      <h6 className="fw-semibold mb-2">
                        <i className="bi bi-2-circle text-primary me-2"></i>Call <code>CloudAyn.identify()</code> after login
                      </h6>
                      <div className="position-relative">
                        <pre className="bg-dark text-light rounded p-3 mb-0" style={{ fontSize: '12px', overflowX: 'auto' }}>{IDENTIFY_CODE}</pre>
                        <button
                          className="btn btn-sm btn-outline-light position-absolute top-0 end-0 m-2"
                          onClick={copyCode}
                          style={{ fontSize: '11px' }}
                        >
                          <i className={`bi ${codeCopied ? 'bi-check-lg' : 'bi-clipboard'} me-1`}></i>
                          {codeCopied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div className="col-12 col-lg-7">
                      <h6 className="fw-semibold mb-2">
                        <i className="bi bi-info-circle text-info me-2"></i>How it works
                      </h6>
                      <ul className="small text-muted ps-3 mb-0">
                        <li className="mb-1">Visitor matched by their browser <code>visitor_id</code> cookie.</li>
                        <li className="mb-1">Once identified, name and email appear in this log.</li>
                        <li className="mb-1">Anonymous visitors show as "Anonymous" until identified.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Visitors Table */}
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            ) : visitors.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-people fs-1 text-muted d-block mb-3"></i>
                <h5>No visitors found</h5>
                <p className="text-muted">
                  {search ? `No results for "${search}"` : 'No visitor data recorded yet'}
                </p>
              </div>
            ) : (
              <>
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">
                      Unique Visitors{' '}
                      {pagination && (
                        <span className="text-muted fw-normal">({pagination.total.toLocaleString()} total)</span>
                      )}
                    </h6>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Visitor</th>
                            <th>Country</th>
                            <th>Device</th>
                            <th>Browser</th>
                            <th>IP</th>
                            <th>First Seen</th>
                            <th>Last Seen</th>
                            <th className="text-end">Views</th>
                            <th>Campaign</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visitors.map(visitor => {
                            const { code, display } = resolveCountry(visitor.country, visitor.country_code);
                            const live = isLiveNow(visitor.last_seen);
                            return (
                              <tr
                                key={visitor.visitor_id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => openDetail(visitor.visitor_id)}
                              >
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <div className="position-relative flex-shrink-0">
                                      <div
                                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                        style={{ width: '36px', height: '36px', fontSize: '14px', backgroundColor: getAvatarColor(visitor.visitor_id) }}
                                      >
                                        {getAvatarLetter(visitor)}
                                      </div>
                                      {live && (
                                        <span style={{
                                          position: 'absolute', bottom: 0, right: 0,
                                          width: '10px', height: '10px', borderRadius: '50%',
                                          background: '#10b981', border: '2px solid white',
                                          animation: 'live-green-pulse 1.5s ease-in-out infinite',
                                        }}></span>
                                      )}
                                    </div>
                                    <div>
                                      <div className="fw-medium d-flex align-items-center gap-1">
                                        {visitor.name || <span className="text-muted">Anonymous</span>}
                                        {live && <LiveBadge />}
                                      </div>
                                      {visitor.email && <small className="text-muted">{visitor.email}</small>}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  {display ? (
                                    <span>
                                      {code ? countryFlag(code) : '🌐'}{' '}
                                      <small className="text-muted">{display}</small>
                                    </span>
                                  ) : <span className="text-muted">—</span>}
                                </td>
                                <td>
                                  <i className={`bi ${getDeviceIcon(visitor.device_type)}`} title={visitor.device_type || undefined}></i>
                                </td>
                                <td><small>{visitor.browser || '—'}</small></td>
                                <td><small className="font-monospace">{visitor.ip_address || '—'}</small></td>
                                <td><small>{formatDate(visitor.first_seen)}</small></td>
                                <td>
                                  <small className={live ? 'text-success fw-semibold' : ''}>
                                    {live ? 'Just now' : formatDate(visitor.last_seen)}
                                  </small>
                                </td>
                                <td className="text-end fw-medium">{visitor.total_page_views}</td>
                                <td>
                                  {visitor.campaign_slug ? (
                                    <span className="badge bg-primary-subtle text-primary">
                                      <i className="bi bi-megaphone me-1"></i>{visitor.campaign_slug}
                                    </span>
                                  ) : <span className="text-muted">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Pagination */}
                {pagination && pagination.total_pages > 1 && (
                  <nav className="mt-3">
                    <ul className="pagination pagination-sm justify-content-center mb-0">
                      <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p - 1)}>
                          <i className="bi bi-chevron-left"></i>
                        </button>
                      </li>
                      {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => (
                        <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page >= pagination.total_pages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p + 1)}>
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ── Visitor Journey Drawer ── */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1040,
            }}
            onClick={closeDetail}
          />

          {/* Drawer panel */}
          <div
            ref={drawerRef}
            style={{
              position: 'fixed', top: 0, right: 0, height: '100vh',
              width: 'min(680px, 96vw)', zIndex: 1050,
              background: 'white', overflowY: 'auto',
              boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Drawer Header */}
            <div style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid #f1f5f9',
              background: 'linear-gradient(135deg, #f8fafc, #fff)',
              flexShrink: 0,
              position: 'sticky', top: 0, zIndex: 2,
            }}>
              <div className="d-flex align-items-center gap-3">
                {visitorDetail && (
                  <div className="position-relative flex-shrink-0">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                      style={{
                        width: '48px', height: '48px', fontSize: '18px',
                        backgroundColor: getAvatarColor(visitorDetail.visitor.visitor_id),
                      }}
                    >
                      {visitorDetail.visitor.name
                        ? visitorDetail.visitor.name.charAt(0).toUpperCase()
                        : '?'}
                    </div>
                    {visitorDetail.visitor.last_seen && isLiveNow(visitorDetail.visitor.last_seen) && (
                      <span style={{
                        position: 'absolute', bottom: 0, right: 0,
                        width: '12px', height: '12px', borderRadius: '50%',
                        background: '#10b981', border: '2px solid white',
                        animation: 'live-green-pulse 1.5s ease-in-out infinite',
                      }}></span>
                    )}
                  </div>
                )}
                {!visitorDetail && !detailLoading && (
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '48px', height: '48px', background: '#f1f5f9' }}
                  >
                    <i className="bi bi-person text-muted fs-5"></i>
                  </div>
                )}

                <div className="flex-grow-1 min-w-0">
                  {detailLoading ? (
                    <div>
                      <div className="placeholder-glow"><span className="placeholder col-6"></span></div>
                      <div className="placeholder-glow mt-1"><span className="placeholder col-4 bg-secondary"></span></div>
                    </div>
                  ) : visitorDetail ? (
                    <>
                      <div className="fw-bold" style={{ fontSize: '17px', color: '#0f172a' }}>
                        {visitorDetail.visitor.name || 'Anonymous Visitor'}
                        {isLiveNow(visitorDetail.visitor.last_seen) && <LiveBadge />}
                      </div>
                      <div className="text-muted" style={{ fontSize: '13px' }}>
                        {visitorDetail.visitor.email && (
                          <span className="me-3"><i className="bi bi-envelope me-1"></i>{visitorDetail.visitor.email}</span>
                        )}
                        {visitorDetail.visitor.ip_address && (
                          <span className="font-monospace">{visitorDetail.visitor.ip_address}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-muted">Visitor Journey</div>
                  )}
                </div>

                <button
                  className="btn-close"
                  onClick={closeDetail}
                  style={{ flexShrink: 0 }}
                ></button>
              </div>
            </div>

            {/* Drawer Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {detailLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="text-muted mt-3 mb-0">Loading visitor journey…</p>
                </div>
              ) : visitorDetail ? (
                <>
                  {/* Info grid */}
                  <div
                    className="row g-2 mb-4 p-3 rounded-3"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                  >
                    {(() => {
                      const v = visitorDetail.visitor;
                      const { code, display } = resolveCountry(v.country, null);
                      const countryVal = display ? `${code ? countryFlag(code) : '🌐'} ${display}${v.city ? ', ' + v.city : ''}` : null;
                      const items = [
                        { icon: 'bi-geo-alt', label: 'Location', value: countryVal },
                        { icon: 'bi-laptop', label: 'Device', value: v.device_type },
                        { icon: 'bi-browser-chrome', label: 'Browser', value: v.browser ? `${v.browser}${v.os ? ' / ' + v.os : ''}` : null },
                        { icon: 'bi-telephone', label: 'Phone', value: v.phone },
                        { icon: 'bi-calendar-check', label: 'First Visit', value: formatDate(v.first_seen) },
                        { icon: 'bi-clock-history', label: 'Last Active', value: isLiveNow(v.last_seen) ? 'Just now' : formatDateTime(v.last_seen) },
                        { icon: 'bi-eye', label: 'Total Pages', value: v.total_page_views ? String(v.total_page_views) : null, bold: true },
                        { icon: 'bi-megaphone', label: 'Campaign', value: v.campaign_slug },
                      ].filter(i => i.value);

                      return items.map(({ icon, label, value, bold }) => (
                        <div className="col-6" key={label}>
                          <div className="d-flex align-items-start gap-2">
                            <i className={`bi ${icon} text-muted mt-1`} style={{ fontSize: '12px', flexShrink: 0 }}></i>
                            <div>
                              <div style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {label}
                              </div>
                              <div style={{ fontSize: '13px', fontWeight: bold ? 700 : 400, color: '#1e293b' }}>
                                {value}
                              </div>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* UTM info */}
                  {(visitorDetail.visitor.utm_source || visitorDetail.visitor.utm_campaign) && (
                    <div className="mb-4 p-3 rounded-3" style={{ background: '#fefce8', border: '1px solid #fef08a' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#92400e', marginBottom: '6px' }}>
                        <i className="bi bi-tags me-1"></i>UTM Parameters
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {visitorDetail.visitor.utm_source && (
                          <span style={{ fontSize: '12px', background: '#fef3c7', color: '#78350f', padding: '2px 8px', borderRadius: '12px' }}>
                            source: {visitorDetail.visitor.utm_source}
                          </span>
                        )}
                        {visitorDetail.visitor.utm_medium && (
                          <span style={{ fontSize: '12px', background: '#fef3c7', color: '#78350f', padding: '2px 8px', borderRadius: '12px' }}>
                            medium: {visitorDetail.visitor.utm_medium}
                          </span>
                        )}
                        {visitorDetail.visitor.utm_campaign && (
                          <span style={{ fontSize: '12px', background: '#fef3c7', color: '#78350f', padding: '2px 8px', borderRadius: '12px' }}>
                            campaign: {visitorDetail.visitor.utm_campaign}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Journey Timeline */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold mb-0" style={{ color: '#0f172a' }}>
                      <i className="bi bi-signpost-split me-2 text-primary"></i>
                      Visitor Journey
                    </h6>
                    <span className="badge bg-primary-subtle text-primary" style={{ fontSize: '11px' }}>
                      {visitorDetail.pages.length} pages
                    </span>
                  </div>

                  {/* Duration legend */}
                  <div className="d-flex flex-wrap gap-2 mb-3" style={{ fontSize: '10px', color: '#94a3b8' }}>
                    {[
                      { color: '#9ca3af', label: '< 30s' },
                      { color: '#3b82f6', label: '30s–2m' },
                      { color: '#10b981', label: '2–5m' },
                      { color: '#8b5cf6', label: '5m+' },
                    ].map(({ color, label }) => (
                      <span key={label} className="d-flex align-items-center gap-1">
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: color, flexShrink: 0, display: 'inline-block' }}></span>
                        {label}
                      </span>
                    ))}
                  </div>

                  <VisitorTimeline detail={visitorDetail} />
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-exclamation-circle d-block mb-2 fs-4"></i>
                  Failed to load visitor data
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Global animation styles */}
      <style>{`
        @keyframes live-green-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
          50% { opacity: 0.8; box-shadow: 0 0 0 4px rgba(16, 185, 129, 0); }
        }
      `}</style>
    </DashboardLayout>
  );
};

export default VisitorLogPage;
